/**
 * `FadeSwap` — keyed FADE-OVER between old and new content.
 *
 * When `swapKey` changes (new data arrived), the incoming content
 * updates IN PLACE (stable key — no remount, no enter animation, always
 * fully opaque) while a snapshot of the outgoing content is kept
 * mounted absolutely positioned ON TOP and fades out. Compositor-only
 * (opacity), tokens from `MOTION` (motion-polish plan §2.1).
 *
 * Why fade-over and not cross-dissolve: with BOTH faces animating
 * (old 1→0 over new 0→1) the two semi-transparent layers let the white
 * card show through at the midpoint — a luminance dip users perceive
 * as a flicker/blink on dense bar/line charts. With the incoming face
 * opaque underneath, every frame composites old(α) over new(1): the
 * mathematically correct dissolve, no dip, ever. The stable key also
 * avoids remounting chart subtrees (SVG teardown + re-measure) on
 * every data landing.
 *
 * Used for: table row blocks after a refetch, chart plots keyed by a
 * data hash (tier-1 chart transition, plan §2.4), KPI delta chips,
 * text blocks.
 *
 * Reduced motion: instant swap (old content dropped immediately).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  SuppressEntrance,
  MOTION,
  ensureMotionStyles,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

export interface FadeSwapProps {
  /** Identity of the content — change it to trigger the crossfade. */
  swapKey: string | number;
  children: ReactNode;
  /** Crossfade duration in ms. Defaults to `MOTION.durBase`. */
  durationMs?: number;
  className?: string;
  /** Extra classes on the INCOMING face wrapper — pass `h-full` when
   *  the child relies on a percentage-height chain (grid-stretched
   *  cards). The outgoing face is absolutely positioned and needs
   *  nothing. */
  innerClassName?: string;
  /** Skip the crossfade entirely (content hard-swaps in place). Trial
   *  toggle 2026-07-23: charts render the new data directly on filter
   *  changes while the user evaluates fade-over vs no transition. */
  disabled?: boolean;
}

interface Outgoing {
  key: string | number;
  node: ReactNode;
}

export function FadeSwap({
  swapKey,
  children,
  durationMs = MOTION.durBase,
  className,
  innerClassName,
  disabled = false,
}: FadeSwapProps): JSX.Element {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();

  // Snapshot of the previous render's key + subtree, taken when the
  // key changes. Refs (not state) so the snapshot is captured during
  // render without an extra commit.
  const lastKeyRef = useRef<string | number>(swapKey);
  const lastNodeRef = useRef<ReactNode>(children);

  const [outgoing, setOutgoing] = useState<Outgoing | null>(null);
  // Bumps on every swap so a rapid re-swap remounts the outgoing face
  // (restarting its exit animation from opacity 1).
  const [swapCount, setSwapCount] = useState(0);

  if (lastKeyRef.current !== swapKey) {
    // Render-phase state update (React's official "adjusting state
    // during render" pattern): React re-renders THIS component
    // synchronously before committing, so the outgoing overlay mounts
    // in the SAME commit as the new content. Deferring the snapshot
    // (the old queueMicrotask) painted one frame of the new content
    // WITHOUT the overlay, then mounted the old face on top — users
    // saw new → old → new on line charts when filtering.
    // No loop: the ref is updated first, so the re-render skips this
    // branch. StrictMode-safe: the double-invoked render sees the ref
    // already matching swapKey → the snapshot is taken exactly once.
    const snapshot: Outgoing = { key: lastKeyRef.current, node: lastNodeRef.current };
    lastKeyRef.current = swapKey;
    if (!reduced && !disabled) {
      setOutgoing(snapshot);
      setSwapCount((c) => c + 1);
    }
  }
  lastNodeRef.current = children;

  // Drop the outgoing face after the crossfade completes. Cleared on
  // unmount and restarted when a newer swap replaces the snapshot.
  useEffect(() => {
    if (outgoing === null) return undefined;
    const t = setTimeout(() => setOutgoing(null), durationMs);
    return () => clearTimeout(t);
  }, [outgoing, durationMs]);

  return (
    <div className={`relative ${className ?? ""}`} data-pm-fadeswap>
      {/* Incoming face: stable key (updates in place — charts/tables are
          NOT remounted on a data landing) and always fully opaque, so
          the fade never lets the card background show through. */}
      <div className={innerClassName}>{children}</div>
      {/* Outgoing face: snapshot of the previous subtree, absolutely
          positioned ON TOP (positioned elements paint above the static
          incoming face), fading 1 → 0. Old pixels cover the new content
          until they dissolve — no luminance dip. */}
      {outgoing !== null && !reduced && (
        <div
          key={`out-${String(outgoing.key)}-${swapCount}`}
          aria-hidden="true"
          data-pm-fadeswap-outgoing
          className="pointer-events-none absolute inset-0"
          style={{
            animation: `pmFadeIn ${durationMs}ms ${MOTION.easeOut} reverse forwards`,
          }}
        >
          {/* The ghost is a fresh mount of the OLD subtree — without
              suppression its fromZero/entrance hooks restart at zero
              counting toward stale data, painted on top of the live
              card (2026-07-23 regression). Snapshots render settled. */}
          <SuppressEntrance>{outgoing.node}</SuppressEntrance>
        </div>
      )}
    </div>
  );
}
